// ✅ src/pages/VehicleV2/LubricationRequest.jsx
// UPDATED FULL FILE
//
// ✅ Updated pattern:
// - common localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - pageLoading + initLoading + itemMasterLoading + mainLoading + submitLoading
// - stable init / top search / filtration / add / submit flow
// - avoids repeated API calls
// - keeps itemCategoryId default as "Select"
// - filtration search sends exact Select values
// - loadInit does NOT overwrite itemCategoryId
//
// FLOW ✅
// - On load: show only 2 containers
// - Top Search -> if already created, load request details into main grid
// - Filtration Search -> shows item master table + Add button
// - Add -> fetch item/detail for selected items -> show main grid
// - Submit -> save master + detail

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaArrowLeft,
  FaSearch,
  FaPlus,
  FaSave,
  FaTrash,
  FaSyncAlt,
  FaInfoCircle,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/lubricationRequest";

/* ---------------- helpers ---------------- */
const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const isSelect = (v) => {
  const s = toStr(v);
  return !s || s.toLowerCase() === "select";
};

const uniqBy = (arr, keyFn) => {
  const seen = new Set();
  return arr.filter((x) => {
    const k = keyFn(x);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

const normalizeOptionList = (arr, fallbackLabel = "Select") => {
  const list = toList(arr).map((x) => ({
    label: toStr(x.label ?? x.name ?? x.text ?? fallbackLabel, fallbackLabel),
    value: String(x.value ?? x.id ?? x.key ?? fallbackLabel),
  }));

  return list.length ? list : [{ value: fallbackLabel, label: fallbackLabel }];
};

const normalizeYearList = (arr) => {
  const list = toList(arr).map((x) => String(x));
  return list.length ? list : ["Select"];
};

const normalizeMainRow = (r) => {
  const itemId =
    r.fld_itemid ?? r.itemid ?? r.ItemId ?? r.fld_itemId ?? r.fld_itemID;
  const uomId =
    r.fld_uomid ?? r.uomid ?? r.UomId ?? r.fld_uomId ?? r.fld_uomID;

  const closingsock = toNum(
    r.closingsock ??
      r.closingstock ??
      r.totalconsumptionpreviousmont ??
      r.totalconsumptionpreviousmonth ??
      r.fld_closing_stock
  );

  const curReq = toNum(
    r.Currentmonthrequirement ??
      r.currentmonthrequirement ??
      r.currentRequirement
  );

  const finalReqRaw =
    r.Currentmonthfinalrequirement ??
    r.currentmonthfinalrequirement ??
    r.finalRequirement;

  const finalReq = Number.isFinite(Number(finalReqRaw))
    ? toNum(finalReqRaw)
    : Math.max(0, closingsock - curReq);

  return {
    fld_itemname:
      r.fld_itemname ?? r.itemname ?? r.ItemName ?? r.itemname ?? "",
    fld_itemid: toInt(itemId),
    fld_uomname: r.fld_uomname ?? r.uomname ?? r.itemuom ?? r.UOM ?? "",
    fld_uomid: toInt(uomId),

    openingqty: toNum(r.openingqty ?? r.openingQty ?? 0),
    received: toNum(r.received ?? 0),
    totalpreviousmonth: toNum(r.totalpreviousmonth ?? 0),
    consumptionpreviousmonth: toNum(r.consumptionpreviousmonth ?? 0),
    closingsock,
    totalconsumptionpreviousmont: toNum(
      r.totalconsumptionpreviousmont ??
        r.totalconsumptionpreviousmonth ??
        closingsock
    ),

    Currentmonthrequirement: curReq,
    Currentmonthfinalrequirement: finalReq,
    Remarks: toStr(r.Remarks ?? r.remarks ?? ""),
  };
};

const calcFinal = (row) =>
  Math.max(0, toNum(row.closingsock) - toNum(row.Currentmonthrequirement));

const extractItemIdFromMasterRow = (r) =>
  r.itemid ?? r.fld_itemid ?? r.itemId ?? r.ItemId ?? r.itemcode ?? r.ItemCode;

const extractDisplayFromMasterRow = (r) => ({
  itemCode: r.itemcode ?? r.fld_itemcode ?? r.ItemCode ?? "",
  itemName: r.itemname ?? r.fld_itemname ?? r.ItemName ?? "",
  partNo: r.itemPartNo ?? r.partno ?? r.fld_partno ?? "",
  uom: r.itemuom ?? r.uomname ?? r.fld_uomname ?? "",
});

export default function LubricationRequest() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, branchType, userId]
  );

  /* ===========================
     nav state
  =========================== */
  const navState = location.state || {};
  const incomingRequestId = navState?.requestId || navState?.id || "";

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const [itemMasterLoading, setItemMasterLoading] = useState(false);
  const [mainLoading, setMainLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const isBusy =
    pageLoading ||
    initLoading ||
    itemMasterLoading ||
    mainLoading ||
    submitLoading;

  /* ===========================
     init dropdowns
  =========================== */
  const [branches, setBranches] = useState([
    { value: "Select", label: "Select" },
  ]);
  const [years, setYears] = useState(["Select"]);
  const [months, setMonths] = useState([
    { value: "Select", label: "Select" },
  ]);
  const [itemCategories, setItemCategories] = useState([
    { value: "Select", label: "Select" },
  ]);
  const [itemNames, setItemNames] = useState([
    { value: "Select", label: "Select" },
  ]);
  const [itemCodes, setItemCodes] = useState([
    { value: "Select", label: "Select" },
  ]);

  /* ===========================
     top filters
  =========================== */
  const [top, setTop] = useState({
    branchId: String(sessionBranchId),
    year: "Select",
    month: "Select",
  });

  /* ===========================
     filtration
  =========================== */
  const [fil, setFil] = useState({
    itemCategoryId: "Select",
    itemName: "Select",
    itemCode: "Select",
  });

  /* ===========================
     ui visibility
  =========================== */
  const [showItemMaster, setShowItemMaster] = useState(false);
  const [showMainGrid, setShowMainGrid] = useState(false);

  /* ===========================
     data tables
  =========================== */
  const [itemMasterRows, setItemMasterRows] = useState([]);
  const [itemSelected, setItemSelected] = useState({});
  const [requestId, setRequestId] = useState("");
  const [mainRows, setMainRows] = useState([]);

  const didLoadRequestRef = useRef(false);

  /* ===========================
     styles
  =========================== */
  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const cardClass = useMemo(
    () =>
      `p-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const sectionClass = useMemo(
    () =>
      `rounded-xl p-6 border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
      }`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  /* ===========================
     handlers
  =========================== */
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const setTopField = useCallback((name, value) => {
    setTop((prev) => ({ ...prev, [name]: value }));
  }, []);

  const setFilField = useCallback((name, value) => {
    setFil((prev) => ({ ...prev, [name]: value }));
  }, []);

  const toggleItem = useCallback((key) => {
    setItemSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const updateMainRow = useCallback((idx, patch) => {
    setMainRows((prev) => {
      const copy = [...prev];
      const row = { ...copy[idx], ...patch };
      row.Currentmonthfinalrequirement = calcFinal(row);
      copy[idx] = row;
      return copy;
    });
  }, []);

  const removeMainRow = useCallback((idx) => {
    setMainRows((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  /* ===========================
     init load
  =========================== */
  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);
      setInitLoading(true);

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchId: sessionBranchId,
          branchType,
          loginUserId,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        toast.error(data?.message || "Init failed");
        return;
      }

      setBranches(normalizeOptionList(data?.branches, "Select"));
      setYears(normalizeYearList(data?.years));
      setMonths(normalizeOptionList(data?.months, "Select"));
      setItemCategories(normalizeOptionList(data?.itemCategories, "Select"));
      setItemNames(normalizeOptionList(data?.itemNames, "Select"));
      setItemCodes(normalizeOptionList(data?.itemCodes, "Select"));

      const d = data?.defaults || {};

      setTop((prev) => ({
        ...prev,
        branchId: String(d.branchId ?? prev.branchId ?? sessionBranchId),
        year: String(d.year ?? prev.year ?? "Select"),
        month: String(d.month ?? prev.month ?? "Select"),
      }));

      // ✅ DO NOT set itemCategoryId from backend defaults
      setFil({
        itemCategoryId: "Select",
        itemName: "Select",
        itemCode: "Select",
      });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Failed to load init"
      );
    } finally {
      setInitLoading(false);
      setPageLoading(false);
    }
  }, [sessionBranchId, branchType, loginUserId, commonHeaders]);

  /* ===========================
     refresh filtration options
  =========================== */
  const refreshFiltrationOptions = useCallback(async () => {
    const branchId = toInt(top.branchId);
    if (!branchId) return;
    if (isSelect(top.month) || isSelect(top.year)) return;

    try {
      const { data } = await axios.get(`${API_BASE}/filtration/options`, {
        params: {
          branchId,
          month: top.month,
          year: top.year,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Failed to refresh options");
      }

      setItemNames(normalizeOptionList(data?.itemNames, "Select"));
      setItemCodes(normalizeOptionList(data?.itemCodes, "Select"));

      setFil((prev) => ({
        ...prev,
        itemName: "Select",
        itemCode: "Select",
        // itemCategoryId intentionally unchanged
      }));
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          err.message ||
          "Failed to refresh filters"
      );
    }
  }, [top.branchId, top.month, top.year, commonHeaders]);

  /* ===========================
     load existing request details
  =========================== */
  const loadRequestDetails = useCallback(
    async (reqId) => {
      if (!reqId) return;

      try {
        setMainLoading(true);

        const { data } = await axios.get(`${API_BASE}/request/details`, {
          params: { requestId: reqId },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load request details");
        }

        const rows = toList(data?.rows)
          .map(normalizeMainRow)
          .map((r) => ({ ...r, Currentmonthfinalrequirement: calcFinal(r) }));

        setRequestId(String(reqId));
        setMainRows(rows);
        setShowMainGrid(true);
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message ||
            err.message ||
            "Failed to load request details"
        );
      } finally {
        setMainLoading(false);
      }
    },
    [commonHeaders]
  );

  /* ===========================
     top search
  =========================== */
  const handleTopSearch = useCallback(async () => {
    const branchId = toInt(top.branchId);
    if (!branchId) return toast.warning("Select Branch");
    if (isSelect(top.month)) return toast.warning("Select Month");
    if (isSelect(top.year)) return toast.warning("Select Year");

    try {
      setMainLoading(true);

      const { data } = await axios.get(`${API_BASE}/search/request`, {
        params: {
          branchId,
          month: top.month,
          year: top.year,
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Search failed");
      }

      if (data?.alreadyCreated) {
        const reqId = String(data?.requestId || "");
        setRequestId(reqId);
        toast.info(data?.message || "Already request created");

        const det = await axios.get(`${API_BASE}/request/details`, {
          params: { requestId: reqId },
          headers: commonHeaders,
        });

        if (det.data?.success) {
          const rows = toList(det.data?.rows)
            .map(normalizeMainRow)
            .map((r) => ({ ...r, Currentmonthfinalrequirement: calcFinal(r) }));

          setMainRows(rows);
          setShowMainGrid(true);
        }

        return;
      }

      const rows = toList(data?.rows)
        .map(normalizeMainRow)
        .map((r) => ({ ...r, Currentmonthfinalrequirement: calcFinal(r) }));

      setRequestId("");
      setMainRows(rows);
      setShowMainGrid(true);
      toast.success("Loaded request data");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Top search failed"
      );
    } finally {
      setMainLoading(false);
    }
  }, [top.branchId, top.month, top.year, commonHeaders]);

  /* ===========================
     filtration search
  =========================== */
  const handleFiltrationSearch = useCallback(async () => {
    const branchId = toInt(top.branchId);
    if (!branchId) return toast.warning("Select Branch");
    if (isSelect(top.month)) return toast.warning("Select Month");
    if (isSelect(top.year)) return toast.warning("Select Year");

    try {
      setItemMasterLoading(true);

      const { data } = await axios.get(`${API_BASE}/filtration/search`, {
        params: {
          branchId,
          itemCategoryId: fil.itemCategoryId || "Select",
          itemCode: fil.itemCode || "Select",
          itemName: fil.itemName || "Select",
        },
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Filtration search failed");
      }

      setItemMasterRows(toList(data?.rows));
      setItemSelected({});
      setShowItemMaster(true);
      toast.success("Items loaded");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          err.message ||
          "Filtration search failed"
      );
    } finally {
      setItemMasterLoading(false);
    }
  }, [top.branchId, top.month, top.year, fil, commonHeaders]);

  /* ===========================
     add selected items
  =========================== */
  const handleAdd = useCallback(async () => {
    const branchId = toInt(top.branchId);
    const selectedKeys = Object.keys(itemSelected).filter((k) => itemSelected[k]);

    if (!selectedKeys.length) {
      return toast.info("Select at least one item");
    }

    try {
      setMainLoading(true);

      const requests = selectedKeys.map((itemId) =>
        axios.get(`${API_BASE}/item/detail`, {
          params: {
            branchId,
            month: top.month,
            year: top.year,
            itemId,
          },
          headers: commonHeaders,
        })
      );

      const results = await Promise.all(requests);

      const newRows = results
        .map((r) => r.data?.row)
        .filter(Boolean)
        .map(normalizeMainRow)
        .map((r) => ({ ...r, Currentmonthfinalrequirement: calcFinal(r) }));

      setMainRows((prev) =>
        uniqBy([...prev, ...newRows], (x) => String(x.fld_itemid))
      );

      setShowMainGrid(true);
      toast.success(`Added ${newRows.length} item(s)`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Add failed");
    } finally {
      setMainLoading(false);
    }
  }, [top.branchId, top.month, top.year, itemSelected, commonHeaders]);

  /* ===========================
     submit
  =========================== */
  const handleSubmit = useCallback(async () => {
    const branchId = toInt(top.branchId);

    if (!branchId) return toast.warning("Select Branch");
    if (isSelect(top.month)) return toast.warning("Select Month");
    if (isSelect(top.year)) return toast.warning("Select Year");
    if (!mainRows.length) return toast.info("No Records Found!");

    try {
      setSubmitLoading(true);

      const payload = {
        requestId: requestId || "",
        branchId,
        month: top.month,
        year: top.year,
        loginUserId,
        items: mainRows.map((r) => ({
          itemId: r.fld_itemid,
          uomId: r.fld_uomid,
          openingqty: r.openingqty,
          received: r.received,
          totalpreviousmonth: r.totalpreviousmonth,
          consumptionpreviousmonth: r.consumptionpreviousmonth,
          closingsock: r.closingsock,
          totalconsumptionpreviousmont:
            r.totalconsumptionpreviousmont ?? r.closingsock,
          currentRequirement: r.Currentmonthrequirement,
          finalRequirement: r.Currentmonthfinalrequirement,
          remarks: r.Remarks,
        })),
      };

      const { data } = await axios.post(`${API_BASE}/submit`, payload, {
        headers: commonHeaders,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Submit failed");
      }

      setRequestId(String(data?.requestId || ""));
      toast.success(data?.message || "Saved successfully");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Submit error"
      );
    } finally {
      setSubmitLoading(false);
    }
  }, [top.branchId, top.month, top.year, mainRows, requestId, loginUserId, commonHeaders]);

  /* ===========================
     refresh
  =========================== */
  const handleRefresh = useCallback(async () => {
    await loadInit();

    if (requestId) {
      await loadRequestDetails(requestId);
    }
  }, [loadInit, requestId, loadRequestDetails]);

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    if (initLoading) return;
    refreshFiltrationOptions();
  }, [initLoading, refreshFiltrationOptions]);

  useEffect(() => {
    if (initLoading) return;
    if (!incomingRequestId) return;
    if (didLoadRequestRef.current) return;

    didLoadRequestRef.current = true;
    loadRequestDetails(incomingRequestId);
  }, [initLoading, incomingRequestId, loadRequestDetails]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">LUBRICATION REQUEST</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`${cardClass} mt-6`}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Lubrication Request
              </h1>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {sessionBranchId || "-"} {" • "} User ID: {loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                {!!requestId && <span className={pillClass}>Request ID: {requestId}</span>}
                <span className={pillClass}>Mode: ENTRY</span>
                <span className={pillClass}>Rows: {mainRows.length}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {(pageLoading || initLoading) ? (
            <div className="text-center py-16">
              <div
                className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                  isDark ? "bg-gray-700" : "bg-gray-100"
                }`}
              >
                <FaInfoCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h3
                className={`text-xl font-semibold mb-2 ${
                  isDark ? "text-gray-200" : "text-gray-800"
                }`}
              >
                Loading...
              </h3>
              <p
                className={`mb-6 max-w-sm mx-auto ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Please wait while data is loading.
              </p>
            </div>
          ) : (
            <>
              {/* LUBRICATION REQUEST */}
              <div className={`${sectionClass} mb-6`}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
                  <div>
                    <label className={labelClass}>Branch</label>
                    <select
                      className={inputClass}
                      value={top.branchId}
                      onChange={(e) => setTopField("branchId", e.target.value)}
                      disabled={branchType !== "HO" || isBusy}
                    >
                      {branches.map((b, idx) => (
                        <option key={`${b.value}-${idx}`} value={b.value}>
                          {b.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Year</label>
                    <select
                      className={inputClass}
                      value={top.year}
                      onChange={(e) => setTopField("year", e.target.value)}
                      disabled={isBusy}
                    >
                      {years.map((y, idx) => (
                        <option key={`${y}-${idx}`} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Month</label>
                    <select
                      className={inputClass}
                      value={top.month}
                      onChange={(e) => setTopField("month", e.target.value)}
                      disabled={isBusy}
                    >
                      <option value="Select">Select</option>
                      {months
                        .filter((m) => m.value !== "Select")
                        .map((m, idx) => (
                          <option key={`${m.value}-${idx}`} value={m.label}>
                            {m.label}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleTopSearch}
                      type="button"
                      disabled={mainLoading || initLoading || submitLoading}
                      className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all ${
                        isBusy ? "opacity-60 cursor-not-allowed" : ""
                      }`}
                    >
                      <FaSearch />
                      {mainLoading ? "Loading..." : "Search"}
                    </button>
                  </div>
                </div>
              </div>

              {/* FILTERATION AREA */}
              <div className={`${sectionClass} mb-6`}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
                  <div>
                    <label className={labelClass}>Item Category</label>
                    <select
                      className={inputClass}
                      value={fil.itemCategoryId}
                      onChange={(e) =>
                        setFilField("itemCategoryId", e.target.value)
                      }
                      disabled={isBusy}
                    >
                      {itemCategories.map((c, idx) => (
                        <option key={`${c.value}-${idx}`} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Item Name</label>
                    <select
                      className={inputClass}
                      value={fil.itemName}
                      onChange={(e) => setFilField("itemName", e.target.value)}
                      disabled={isBusy}
                    >
                      {itemNames.map((x, idx) => (
                        <option key={`${x.value}-${idx}`} value={x.value}>
                          {x.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>Item Code</label>
                    <select
                      className={inputClass}
                      value={fil.itemCode}
                      onChange={(e) => setFilField("itemCode", e.target.value)}
                      disabled={isBusy}
                    >
                      {itemCodes.map((x, idx) => (
                        <option key={`${x.value}-${idx}`} value={x.value}>
                          {x.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleFiltrationSearch}
                      type="button"
                      disabled={itemMasterLoading || initLoading || mainLoading || submitLoading}
                      className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all ${
                        isBusy ? "opacity-60 cursor-not-allowed" : ""
                      }`}
                    >
                      <FaSearch />
                      {itemMasterLoading ? "Loading..." : "Search"}
                    </button>
                  </div>
                </div>
              </div>

              {/* ITEM MASTER TABLE */}
              {showItemMaster && (
                <div className={`${sectionClass} mb-6`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3
                      className={`text-lg font-semibold ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Items ({itemMasterRows.length})
                    </h3>

                    <button
                      onClick={handleAdd}
                      type="button"
                      disabled={isBusy}
                      className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all ${
                        isBusy ? "opacity-60 cursor-not-allowed" : ""
                      }`}
                    >
                      <FaPlus /> Add
                    </button>
                  </div>

                  <div className="overflow-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="min-w-[950px] w-full text-sm">
                      <thead
                        className={`${
                          isDark
                            ? "bg-gray-900 text-gray-100"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        <tr>
                          <th className="p-3 text-left">S.No</th>
                          <th className="p-3 text-left">Item Code</th>
                          <th className="p-3 text-left">Item Name</th>
                          <th className="p-3 text-left">Part No</th>
                          <th className="p-3 text-left">UOM</th>
                          <th className="p-3 text-center">Select</th>
                        </tr>
                      </thead>

                      <tbody>
                        {itemMasterRows.length === 0 ? (
                          <tr>
                            <td className="p-4 text-center" colSpan={6}>
                              No Records Found!
                            </td>
                          </tr>
                        ) : (
                          itemMasterRows.map((r, idx) => {
                            const itemId = String(extractItemIdFromMasterRow(r));
                            const d = extractDisplayFromMasterRow(r);

                            return (
                              <tr
                                key={`${itemId}-${idx}`}
                                className="border-t border-gray-200 dark:border-gray-700"
                              >
                                <td className="p-3">{idx + 1}</td>
                                <td className="p-3">{d.itemCode || itemId}</td>
                                <td className="p-3">{d.itemName}</td>
                                <td className="p-3">{d.partNo}</td>
                                <td className="p-3">{d.uom}</td>
                                <td className="p-3 text-center">
                                  <input
                                    type="checkbox"
                                    checked={!!itemSelected[itemId]}
                                    onChange={() => toggleItem(itemId)}
                                    disabled={isBusy}
                                  />
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* MAIN GRID */}
              {showMainGrid && (
                <div className={`${sectionClass}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3
                      className={`text-lg font-semibold ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Request Grid ({mainRows.length})
                    </h3>

                    <button
                      onClick={handleSubmit}
                      type="button"
                      disabled={isBusy}
                      className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all ${
                        isBusy ? "opacity-60 cursor-not-allowed" : ""
                      }`}
                    >
                      <FaSave />
                      {submitLoading ? "Submitting..." : "Submit"}
                    </button>
                  </div>

                  <div className="overflow-auto rounded-lg border border-gray-200 dark:border-gray-700">
                    <table className="min-w-[1350px] w-full text-sm">
                      <thead
                        className={`${
                          isDark
                            ? "bg-gray-900 text-gray-100"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        <tr>
                          <th className="p-3 text-left">Item</th>
                          <th className="p-3 text-left">UOM</th>
                          <th className="p-3 text-right">Opening</th>
                          <th className="p-3 text-right">Received</th>
                          <th className="p-3 text-right">Total</th>
                          <th className="p-3 text-right">Consumption</th>
                          <th className="p-3 text-right">Consumption Stock</th>
                          <th className="p-3 text-right">Current Req</th>
                          <th className="p-3 text-right">Final Req</th>
                          <th className="p-3 text-left">Remarks</th>
                          <th className="p-3 text-center">Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {mainRows.length === 0 ? (
                          <tr>
                            <td className="p-4 text-center" colSpan={11}>
                              No Records Found!
                            </td>
                          </tr>
                        ) : (
                          mainRows.map((r, idx) => (
                            <tr
                              key={`${r.fld_itemid}-${idx}`}
                              className="border-t border-gray-200 dark:border-gray-700"
                            >
                              <td className="p-3">
                                <div className="font-semibold">{r.fld_itemname}</div>
                                <div className="text-xs opacity-70">
                                  ItemID: {r.fld_itemid}
                                </div>
                              </td>

                              <td className="p-3">{r.fld_uomname}</td>

                              <td className="p-3 text-right">
                                {toNum(r.openingqty).toFixed(3)}
                              </td>
                              <td className="p-3 text-right">
                                {toNum(r.received).toFixed(3)}
                              </td>
                              <td className="p-3 text-right">
                                {toNum(r.totalpreviousmonth).toFixed(3)}
                              </td>
                              <td className="p-3 text-right">
                                {toNum(r.consumptionpreviousmonth).toFixed(3)}
                              </td>
                              <td className="p-3 text-right">
                                {toNum(r.closingsock).toFixed(3)}
                              </td>

                              <td className="p-3 text-right">
                                <input
                                  className={`${inputClass} text-right`}
                                  value={r.Currentmonthrequirement}
                                  onChange={(e) =>
                                    updateMainRow(idx, {
                                      Currentmonthrequirement: toNum(
                                        e.target.value
                                      ),
                                    })
                                  }
                                  disabled={isBusy}
                                />
                              </td>

                              <td className="p-3 text-right">
                                <input
                                  className={`${inputClass} text-right opacity-70`}
                                  value={r.Currentmonthfinalrequirement}
                                  readOnly
                                />
                              </td>

                              <td className="p-3">
                                <input
                                  className={inputClass}
                                  value={r.Remarks}
                                  onChange={(e) =>
                                    updateMainRow(idx, {
                                      Remarks: e.target.value,
                                    })
                                  }
                                  disabled={isBusy}
                                />
                              </td>

                              <td className="p-3 text-center">
                                <button
                                  onClick={() => removeMainRow(idx)}
                                  type="button"
                                  disabled={isBusy}
                                  className={`inline-flex items-center gap-2 px-3 py-2 rounded ${
                                    isDark
                                      ? "bg-red-900/40 hover:bg-red-900/60 text-white"
                                      : "bg-red-100 hover:bg-red-200 text-red-700"
                                  } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
                                  title="Remove row"
                                >
                                  <FaTrash />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {isBusy && (
                <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  {pageLoading
                    ? "Loading page..."
                    : initLoading
                    ? "Loading initial data..."
                    : itemMasterLoading
                    ? "Loading item master..."
                    : mainLoading
                    ? "Loading request data..."
                    : submitLoading
                    ? "Submitting..."
                    : "Loading..."}
                </div>
              )}
            </>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}